import React, { useState, useEffect, useCallback } from 'react';
import { testimonials } from '../../data/testimonials';
import './Testimonials.scss';

const AUTO_SCROLL_MS = 4500;

const getItemsPerView = () => {
  if (window.innerWidth < 768) return 1;
  if (window.innerWidth < 992) return 2;
  return 3;
};

const StarRating = ({ rating }) => (
  <div className="stars">
    {Array.from({ length: rating }).map((_, i) => (
      <span key={i} className="star">★</span>
    ))}
  </div>
);

const TestimonialCard = ({ avatar, name, role, company, rating, quote }) => (
  <div className="testimonial-card">
    <div className="quote-icon">&ldquo;</div>
    <StarRating rating={rating} />
    <p className="quote-text">{quote}</p>
    <div className="reviewer">
      <div className="avatar">{avatar}</div>
      <div className="reviewer-info">
        <div className="name">{name}</div>
        <div className="role">
          {role} · {company}
        </div>
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(getItemsPerView);
  const [paused, setPaused] = useState(false);

  const maxIndex = Math.max(0, testimonials.length - itemsPerView);
  const totalSlides = maxIndex + 1;

  useEffect(() => {
    const onResize = () => setItemsPerView(getItemsPerView());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setCurrent(prev => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (paused || totalSlides <= 1) return undefined;

    const timer = setInterval(() => {
      setCurrent(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, AUTO_SCROLL_MS);

    return () => clearInterval(timer);
  }, [maxIndex, paused, totalSlides]);

  const goTo = useCallback((index) => {
    setCurrent(Math.max(0, Math.min(index, maxIndex)));
  }, [maxIndex]);

  const slideOffset = (100 / itemsPerView) * current;

  return (
    <section className="testimonials-section" id="testimonials">
      <div className="container">
        <div className="section-header">
          <span className="badge-label">Testimonials</span>
          <h2>What Our Clients Say</h2>
          <div className="section-divider" />
          <p>
            Real feedback from warehouse owners, factory supervisors, and logistics
            teams across Gujarat and India who use Aavjo Industries equipment daily.
          </p>
        </div>

        <div
          className="testimonials-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div
            className="testimonials-track"
            style={{
              '--items-per-view': itemsPerView,
              transform: `translateX(-${slideOffset}%)`,
            }}
          >
            {testimonials.map(t => (
              <div key={t.id} className="testimonials-slide">
                <TestimonialCard {...t} />
              </div>
            ))}
          </div>

          {totalSlides > 1 && (
            <div className="carousel-controls">
              <button
                type="button"
                className="carousel-btn carousel-btn--prev"
                aria-label="Previous testimonials"
                onClick={() => goTo(current <= 0 ? maxIndex : current - 1)}
              >
                ‹
              </button>

              <div className="carousel-dots" role="tablist" aria-label="Testimonial slides">
                {Array.from({ length: totalSlides }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === current}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`carousel-dot${i === current ? ' active' : ''}`}
                    onClick={() => goTo(i)}
                  />
                ))}
              </div>

              <button
                type="button"
                className="carousel-btn carousel-btn--next"
                aria-label="Next testimonials"
                onClick={() => goTo(current >= maxIndex ? 0 : current + 1)}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
